import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { withRouter, Link } from 'react-router-dom';
import { getCookie } from '../../../util_functions';
import {
  MENU_SECTIONS,
  visible_sections,
  filter_sections,
  active_item,
  item_key,
  item_target
} from './menu_config';

import './drawer.css';

const OPEN_SECTIONS_KEY = 'hridaya_drawer_open_sections';

// People starts open; a section the user opened or closed keeps that choice.
const DEFAULT_OPEN_SECTIONS = { people: true };

// Which sections were left open, over the defaults; defaults only when storage is unavailable.
const read_open_sections = () => {
  try {
    return {
      ...DEFAULT_OPEN_SECTIONS,
      ...(JSON.parse(localStorage.getItem(OPEN_SECTIONS_KEY)) || {})
    };
  } catch (e) {
    return { ...DEFAULT_OPEN_SECTIONS };
  }
};

function Drawer({ access_pages, log_out, toggle_drawer, location, drawer_open = true }) {
  const [open, set_open] = useState(read_open_sections);
  const [query, set_query] = useState('');
  const search_ref = useRef(null);

  const sections = useMemo(() => visible_sections(MENU_SECTIONS, access_pages), [access_pages]);
  const active = useMemo(
    () => active_item(sections, location.pathname),
    [sections, location.pathname]
  );
  const shown = useMemo(() => filter_sections(sections, query), [sections, query]);
  const searching = query.trim() !== '';

  // The current page's section opens on load and on navigation.
  useEffect(() => {
    if (!active.section_id) return;
    set_open((prev) => (prev[active.section_id] ? prev : { ...prev, [active.section_id]: true }));
  }, [active.section_id]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify(open));
    } catch (e) {
      // Storage blocked: sections just won't be remembered.
    }
  }, [open]);

  // "/" focuses the search box while the drawer is open, unless the user is typing or holds a modifier.
  useEffect(() => {
    if (!drawer_open) return undefined;
    const on_key = (e) => {
      const tag = (e.target.tagName || '').toLowerCase();
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      if (tag === 'input' || tag === 'textarea' || e.target.isContentEditable) return;
      e.preventDefault();
      if (search_ref.current) search_ref.current.focus();
    };
    document.addEventListener('keydown', on_key);
    return () => document.removeEventListener('keydown', on_key);
  }, [drawer_open]);

  const on_link_click = () => {
    if (window.innerWidth <= 700) toggle_drawer();
  };

  const actions = {
    open_student_chat: () => window.open('/student_chat', '_blank'),
    open_teacher_chat: () => window.open('/teacher_chat', '_blank'),
    log_out: () => log_out()
  };

  const toggle_section = (id) => set_open((prev) => ({ ...prev, [id]: !prev[id] }));

  const render_item = (item) => {
    const key = item_key(item);
    const class_name = `drawer-item${key === active.key ? ' is-active' : ''}`;
    if (item.action) {
      return (
        <button type="button" key={key} className={class_name} onClick={actions[item.action]}>
          {item.name}
        </button>
      );
    }
    if (item.ext) {
      return (
        <a
          key={key}
          href={item.ext}
          target="_blank"
          rel="noopener noreferrer"
          className={class_name}
        >
          {item.name}
          <span className="drawer-item-ext" aria-hidden="true">
            ↗
          </span>
        </a>
      );
    }
    return (
      <Link
        key={key}
        to={item_target(item)}
        className={class_name}
        onClick={on_link_click}
        aria-current={key === active.key ? 'page' : undefined}
      >
        {item.name}
      </Link>
    );
  };

  const render_section = (section) => {
    const is_open = searching || !!open[section.id];
    const count = section.items.filter((item) => !item.label).length;
    const Icon = section.icon;
    const holds_current = section.id === active.section_id;
    return (
      <div key={section.id} className="drawer-section">
        <button
          type="button"
          className={`drawer-section-btn${holds_current ? ' has-current' : ''}`}
          aria-expanded={is_open}
          onClick={() => toggle_section(section.id)}
          disabled={searching}
        >
          <span className="drawer-section-title">
            {Icon ? (
              <Icon
                className="drawer-section-icon"
                size={16}
                strokeWidth={1.8}
                aria-hidden="true"
              />
            ) : null}
            {section.title}
            <span className="drawer-section-count">{count}</span>
          </span>
          {!searching ? (
            <span className={`drawer-section-chev${is_open ? ' is-open' : ''}`}>▾</span>
          ) : null}
        </button>
        {is_open
          ? section.items.map((item) =>
              item.label ? (
                <div key={`label-${item.label}`} className="drawer-sublabel">
                  {item.label}
                </div>
              ) : (
                render_item(item)
              )
            )
          : null}
      </div>
    );
  };

  return (
    <div className="main-drawer">
      <div className="main-drawer-inner">
        <div className="main-drawer-profile">
          <p>{getCookie('cms_email')}</p>
        </div>
        <div className="drawer-search">
          <span className="drawer-search-icon" aria-hidden="true">
            ⌕
          </span>
          <input
            ref={search_ref}
            type="search"
            value={query}
            onChange={(e) => set_query(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') set_query('');
            }}
            placeholder="Search pages…  /"
            aria-label="Search pages"
          />
        </div>
        {shown.length ? (
          shown.map(render_section)
        ) : (
          <div className="drawer-empty">No page matches &ldquo;{query.trim()}&rdquo;.</div>
        )}
      </div>
      <div className="drawer-app-ver">v {import.meta.env.VITE_APP_VER}</div>
    </div>
  );
}

Drawer.propTypes = {
  access_pages: PropTypes.arrayOf(PropTypes.string),
  log_out: PropTypes.func.isRequired,
  toggle_drawer: PropTypes.func.isRequired,
  location: PropTypes.shape({ pathname: PropTypes.string }).isRequired
};

Drawer.defaultProps = {
  access_pages: []
};

export default withRouter(Drawer);
